import type { Variants } from 'framer-motion'
import { AnimatePresence, m } from 'framer-motion'
import { useSettings } from 'hooks/settings'
import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

export function SlideProjector({
  index,
  children,
  className,
  disableAnimations,
}: {
  index: number
  children: React.ReactNode
  className?: string
  disableAnimations?: boolean
}) {
  const { animationsEnabled } = useSettings()
  if (!animationsEnabled || disableAnimations)
    return (
      <div
        className={classNames(
          `relative h-full w-full md:overflow-y-auto`,
          className
        )}
      >
        <div className="absolute top-0 h-full w-full bg-core-tertiary">
          {children}
        </div>
      </div>
    )

  return (
    <SlideAnimation className={className} index={index}>
      {children}
    </SlideAnimation>
  )
}

const variants: Variants = {
  enter: (direction: string) => {
    return {
      x: direction === 'left' ? '125%' : '-125%',
    }
  },
  center: {
    x: 0,
  },
  exit: (direction: string) => {
    return {
      x: direction === 'left' ? '-100%' : '100%',
    }
  },
}

function SlideAnimation({
  index,
  children,
  className,
}: {
  index: number
  children: React.ReactNode
  className?: string
}) {
  const [state, setState] = useState<{
    index: number
    direction: 'left' | 'right'
    children: React.ReactNode
  }>({
    index: index,
    direction: 'left',
    children: children,
  })

  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (index === state.index) return

    setState({
      index: index,
      direction: index > state.index ? 'left' : 'right',
      children: children,
    })
  }, [index, children, state])

  const rootRef = useRef<HTMLDivElement | null>(null)

  return (
    <div
      className={classNames(
        'relative h-full w-full md:overflow-y-auto',
        isAnimating ? 'md:overflow-x-hidden' : 'md:overflow-x-visible',
        className
      )}
      ref={rootRef}
    >
      <AnimatePresence mode="sync" initial={false} custom={state.direction}>
        <m.div
          key={state.index.toString()}
          variants={variants}
          custom={state.direction}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute top-0 h-full w-full"
          transition={{ duration: 0.3 }}
          onAnimationStart={() => setIsAnimating(true)}
          onAnimationComplete={() => setIsAnimating(false)}
          transformTemplate={(_, generatedTransform) => {
            if (generatedTransform === 'translateX(0%) translateZ(0)') {
              return 'none'
            }
            return generatedTransform
          }}
        >
          {state.children}
        </m.div>
      </AnimatePresence>
    </div>
  )
}
